import type { I18nContextValue } from '../../types/i18nContext';
import { formatIso8601Duration, formatNumber } from '../../utils/formatUtils';
import {
  CRAWL_PAGE_STATUSES,
  CRAWL_STAGING_DOCUMENT_STATUSES,
  getCrawlSectionProgress,
} from './crawlProgress';

type Translate = I18nContextValue['translate'];

export type CrawlDetailLine = {
  text: string;
  indent?: boolean;
};

type CrawlPhaseDurations = {
  mappingDuration?: string | null;
  crawlingDuration?: string | null;
  embeddingDuration?: string | null;
};

export function appendMappedUrlCountDetailLine(
  lines: CrawlDetailLine[],
  mappedUrlCount: number | null | undefined,
  options: {
    translate: Translate;
    locale: string;
    selectedUrlCount?: number | null;
  },
) {
  if (mappedUrlCount == null) {
    return;
  }

  const { translate, locale, selectedUrlCount } = options;

  lines.push({
    text: translate('knowledge.crawlMetaMappedUrls', {
      count: formatNumber(mappedUrlCount, locale),
    }),
  });

  lines.push({
    text: translate('knowledge.crawlMetaSelectedUrls', {
      count: formatNumber(selectedUrlCount ?? 0, locale),
    }),
  });
}

export function appendCrawlPhaseDurationDetailLines(
  lines: CrawlDetailLine[],
  durations: CrawlPhaseDurations,
  options: { translate: Translate; locale: string },
) {
  const { translate, locale } = options;

  const mappingDuration = formatIso8601Duration(
    durations.mappingDuration,
    locale,
  );
  if (mappingDuration != null) {
    lines.push({
      text: translate('knowledge.crawlMetaMappingDuration', {
        duration: mappingDuration,
      }),
    });
  }

  const crawlingDuration = formatIso8601Duration(
    durations.crawlingDuration,
    locale,
  );
  if (crawlingDuration != null) {
    lines.push({
      text: translate('knowledge.crawlMetaCrawlingDuration', {
        duration: crawlingDuration,
      }),
    });
  }

  const embeddingDuration = formatIso8601Duration(
    durations.embeddingDuration,
    locale,
  );
  if (embeddingDuration != null) {
    lines.push({
      text: translate('knowledge.crawlMetaEmbeddingDuration', {
        duration: embeddingDuration,
      }),
    });
  }
}

export const CRAWL_PAGE_STATUS_KEYS: Record<
  (typeof CRAWL_PAGE_STATUSES)[number],
  Parameters<Translate>[0]
> = {
  PENDING: 'knowledge.crawlPageStatus.PENDING',
  PROCESSING: 'knowledge.crawlPageStatus.PROCESSING',
  COMPLETED: 'knowledge.crawlPageStatus.COMPLETED',
  FAILED: 'knowledge.crawlPageStatus.FAILED',
};

export const STAGING_DOCUMENT_STATUS_KEYS: Record<
  (typeof CRAWL_STAGING_DOCUMENT_STATUSES)[number],
  Parameters<Translate>[0]
> = {
  PENDING: 'knowledge.documentStatus.PENDING',
  SCANNING: 'knowledge.documentStatus.SCANNING',
  UPLOADING: 'knowledge.documentStatus.UPLOADING',
  EXTRACTING: 'knowledge.documentStatus.EXTRACTING',
  EMBEDDING: 'knowledge.documentStatus.EMBEDDING',
  READY: 'knowledge.documentStatus.READY',
  FAILED: 'knowledge.documentStatus.FAILED',
};

export function appendStatusCountSection(
  lines: CrawlDetailLine[],
  counts:
    | ReadonlyArray<{ count?: number | null; status?: string | null }>
    | null
    | undefined,
  options: {
    translate: Translate;
    headerKey: Parameters<Translate>[0];
    statusKeys: Record<string, Parameters<Translate>[0]>;
    statuses: readonly string[];
    doneStatuses: readonly string[];
    /** When false, only the done summary line is shown (no per-status rows). */
    includeStatusBreakdown?: boolean;
  },
) {
  const progress = getCrawlSectionProgress(counts, {
    statuses: options.statuses,
    doneStatuses: options.doneStatuses,
  });

  if (progress == null) {
    return;
  }

  const {
    translate,
    headerKey,
    statusKeys,
    statuses,
    includeStatusBreakdown = true,
  } = options;
  const byStatus = new Map<string, number>();

  for (const entry of counts ?? []) {
    if (entry.status != null) {
      byStatus.set(entry.status, entry.count ?? 0);
    }
  }

  lines.push({
    text: translate(headerKey, {
      count: String(progress.done),
      percent: String(progress.percent),
    }),
  });

  if (!includeStatusBreakdown) {
    return;
  }

  for (const status of statuses) {
    const count = byStatus.get(status) ?? 0;

    const statusKey = statusKeys[status];
    if (!statusKey) {
      continue;
    }

    lines.push({
      text: translate('knowledge.crawlStatStatusCountLine', {
        status: translate(statusKey),
        count: String(count),
      }),
      indent: true,
    });
  }
}
